import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { BoxSchemeDefinition, BoxSchemeName } from "./constants/box-types";
import { BOX_SCHEME_CONFIG, BOX_SCHEME_MAP } from "./constants/box-constants";
import { BoxSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import {
  BOX_DEFAULT_CONTENT_HORIZONTAL_ALIGNMENT,
  BOX_DEFAULT_CONTENT_VERTICAL_ALIGNMENT,
  BoxContentHorizontalAlignment,
  BoxContentVerticalAlignment
} from "../../constants/global-constants";
import { BackgroundColor } from "../background/constants/background-types";
// import { InitLogging } from "../../decorators/init-logging";
import { getNestedChildrenOf } from "../../utils/dom-helper";
import { StyledComponent } from "../../decorators/styled-component";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-box",
  styleUrl: "box.scss",
  shadow: true
})
export class Box
  implements ColorableComponent<BoxSchemeConfiguration<BoxSchemeDefinition>>, ContainerBreakpointComponent {
  // @InitLogging() sentryTransaction;
  schemeConfig: BoxSchemeConfiguration<BoxSchemeDefinition> = BOX_SCHEME_CONFIG;
  schemeMap = BOX_SCHEME_MAP;
  private slottedHeadlines: Array<HTMLEonUiHeadlineElement>;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: BoxSchemeDefinition;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiBoxElement;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: BoxSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Defines content vertical alignment
   */
  // eslint-disable-next-line @stencil-community/decorators-style
  @Prop({ mutable: true })
  contentVerticalAlignment: BoxContentVerticalAlignment = BOX_DEFAULT_CONTENT_VERTICAL_ALIGNMENT;

  /**
   * Defines content vertical alignment
   */
  // eslint-disable-next-line @stencil-community/decorators-style
  @Prop({ mutable: true })
  contentHorizontalAlignment: BoxContentHorizontalAlignment = BOX_DEFAULT_CONTENT_HORIZONTAL_ALIGNMENT;

  /**
   * Marks a box as active so that the border and background color is highlighted
   */
  @Prop({ reflect: true }) active: boolean;

  /**
   * Set Layout of form wrapper
   * offset set to true means the content aligned layout is applied
   * offset set to false means the background aligned layout is applied
   */
  @Prop({ reflect: true }) offset: boolean = false;

  /**
   * Emitted as soon as the innerBackground property changes.
   */
  @Event({ bubbles: false }) innerBackgroundUpdate: EventEmitter;

  componentWillLoad() {
    this.slottedHeadlines = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-headline"));
    this.slottedHeadlines.forEach((headline) => {
      headline.setAttribute("ignore-alignment", "true");
    });
    this.applyScheme();
    this.initContainerBreakpoints();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  @Watch("active")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
    this.innerBackgroundUpdate.emit(this.innerBackground);
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            box: true,
            active: this.active,
            [`offset`]: this.offset,
            [`vertical-alignment-${this.contentVerticalAlignment}`]: !!this.contentVerticalAlignment,
            [`horizontal-alignment-${this.contentHorizontalAlignment}`]: !!this.contentHorizontalAlignment,
            gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
          })}
        >
          <slot />
        </div>
      </Host>
    );
  }
}
